import type { NurseryCustomer } from '../types/nursery';

/** The order states are listed in: the usual one, not alphabetical. */
export const AU_STATES = ['VIC', 'NSW', 'QLD', 'SA', 'WA', 'TAS', 'NT', 'ACT'] as const;

export type CustomerSort = 'name' | 'name_desc' | 'state' | 'newest';
/** '' = every state; 'none' = customers whose state is not known. */
export type StateFilter = string;

export const SORT_LABELS: Record<CustomerSort, string> = {
  name: 'Name A–Z',
  name_desc: 'Name Z–A',
  state: 'State',
  newest: 'Newest first',
};

export const isCustomerSort = (v: string | null): v is CustomerSort =>
  v === 'name' || v === 'name_desc' || v === 'state' || v === 'newest';

const byName = (a: NurseryCustomer, b: NurseryCustomer) =>
  a.name.localeCompare(b.name, undefined, { sensitivity: 'base', numeric: true });

const stateRank = (s: string | null | undefined) => {
  const i = s ? (AU_STATES as readonly string[]).indexOf(s) : -1;
  return i === -1 ? AU_STATES.length : i;
};

export function compareCustomers(sort: CustomerSort) {
  return (a: NurseryCustomer, b: NurseryCustomer) => {
    switch (sort) {
      case 'name_desc': return byName(b, a);
      case 'state': return stateRank(a.state) - stateRank(b.state) || byName(a, b);
      case 'newest': return String(b.created_at).localeCompare(String(a.created_at)) || byName(a, b);
      default: return byName(a, b);
    }
  };
}

export const matchesState = (c: NurseryCustomer, filter: StateFilter) =>
  !filter || (filter === 'none' ? !c.state : c.state === filter);

/** How many customers (sites included) are in each state, and with none. */
export function stateCounts(customers: NurseryCustomer[]) {
  const counts = new Map<string, number>();
  for (const c of customers) {
    const key = c.state || 'none';
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  return counts;
}

export interface CustomerListView {
  /** Head offices and stand-alone customers, in the chosen order. */
  topLevel: NurseryCustomer[];
  /** The sites to show under each head office, in the chosen order. */
  sitesOf: Map<number, NurseryCustomer[]>;
  /** Customers showing, sites included. */
  shown: number;
}

/**
 * Sites sit under their head office. A site whose head office is not in the
 * list (a search matched the site alone) stands on its own row. With a state
 * chosen, a head office stays when it or any of its sites is in that state,
 * and only those sites show: a Bunnings head office in VIC still shows its
 * WA stores under WA.
 */
export function buildCustomerListView(
  customers: NurseryCustomer[],
  sort: CustomerSort,
  filter: StateFilter,
): CustomerListView {
  const ids = new Set(customers.map(c => c.id));
  const allSites = new Map<number, NurseryCustomer[]>();
  const top: NurseryCustomer[] = [];
  for (const c of customers) {
    if (c.parent_customer_id && ids.has(c.parent_customer_id)) {
      const list = allSites.get(c.parent_customer_id) ?? [];
      list.push(c);
      allSites.set(c.parent_customer_id, list);
    } else {
      top.push(c);
    }
  }
  const cmp = compareCustomers(sort);
  // Sites read best by name under their head office, except when sorting by state.
  const siteCmp = sort === 'state' ? cmp : compareCustomers(sort === 'name_desc' ? 'name_desc' : 'name');
  const sitesOf = new Map<number, NurseryCustomer[]>();
  const topLevel: NurseryCustomer[] = [];
  let shown = 0;
  for (const c of top) {
    const sites = (allSites.get(c.id) ?? []).filter(s => matchesState(s, filter)).sort(siteCmp);
    if (!matchesState(c, filter) && sites.length === 0) continue;
    topLevel.push(c);
    if (sites.length) sitesOf.set(c.id, sites);
    shown += 1 + sites.length;
  }
  topLevel.sort(cmp);
  return { topLevel, sitesOf, shown };
}

/** The heading a customer falls under when sorted by state. */
export const stateHeading = (state: string | null | undefined) => state || 'State not known';
